import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, imgSrc, money, errMsg } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import TabHeader from "@/components/admin/TabHeader";
import ProductForm from "@/components/admin/ProductForm";

function Row({ p, rooms, onEdit, onToggle, onDelete }) {
  return (
    <tr data-testid={`admin-product-row-${p.id}`} className="border-b border-hairline">
      <td className="py-3 pr-4"><img src={imgSrc(p.images[0])} alt="" className="h-16 w-12 bg-[#333336] object-cover object-top" /></td>
      <td className="py-3 pr-4 font-body text-sm text-ink">{p.name}<p className="text-xs text-muted">{p.kind === "ready-to-wear" ? "Ready to wear" : "Made to order"}</p></td>
      <td className="hidden py-3 pr-4 font-body text-sm text-stone md:table-cell">{rooms[p.category] || p.category}</td>
      <td className="py-3 pr-4 font-body text-sm text-ink">{money(p.price)}</td>
      <td className="hidden py-3 pr-4 font-body text-sm text-stone sm:table-cell">{p.stock == null ? "Unlimited" : p.stock}</td>
      <td className="py-3 pr-4"><Switch data-testid={`admin-product-published-${p.id}`} checked={p.published} onCheckedChange={onToggle} /></td>
      <td className="py-3 text-right whitespace-nowrap">
        <button type="button" data-testid={`admin-product-edit-${p.id}`} onClick={onEdit} className="p-2 text-stone hover:text-ink" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
        <button type="button" data-testid={`admin-product-delete-${p.id}`} onClick={onDelete} className="p-2 text-stone hover:text-red-700" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
      </td>
    </tr>
  );
}

export default function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [p, c] = await Promise.all([api.get("/admin/products"), api.get("/store/catalog")]);
    setProducts(p.data);
    setCats(c.data.categories);
  };
  useEffect(() => { load(); }, []);

  const rooms = Object.fromEntries(cats.map((c) => [c.id, c.title]));
  const save = async (p) => {
    try {
      if (p.id) await api.put(`/admin/products/${p.id}`, p);
      else await api.post("/admin/products", p);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = (p) => save({ ...p, published: !p.published });
  const del = async (p) => {
    if (!window.confirm(`Delete “${p.name}”? This can’t be undone.`)) return;
    await api.delete(`/admin/products/${p.id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="admin-products-tab">
      <TabHeader title="Store items" note="Add pieces, change photos, prices and stock. Leave the price empty to show “Price on request”. Switch a piece off to hide it from the store and the Facebook / Instagram feed.">
        <button type="button" data-testid="admin-product-new" onClick={() => setEditing({})} className="inline-flex items-center gap-2 bg-ink px-5 py-3 text-paper hover:bg-ink/85">
          <Plus className="h-4 w-4" /> <span className="overline">New item</span>
        </button>
      </TabHeader>
      <div className="overflow-x-auto bg-paper px-5">
        <table className="w-full">
          <thead>
            <tr className="border-b border-hairline text-left">
              {["", "Item", "Room", "Price", "Stock", "Live", ""].map((h, i) => (
                <th key={i} className={`overline py-3 pr-4 text-[0.58rem] text-muted ${h === "Room" ? "hidden md:table-cell" : h === "Stock" ? "hidden sm:table-cell" : ""}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {products.map((p) => <Row key={p.id} p={p} rooms={rooms} onEdit={() => setEditing(p)} onToggle={() => toggle(p)} onDelete={() => del(p)} />)}
          </tbody>
        </table>
      </div>
      {editing && <ProductForm product={editing} cats={cats} onSave={save} onClose={() => setEditing(null)} />}
    </div>
  );
}
